import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';
import { LAGEN } from '../lagen';

interface DialogContextValue {
  descriptionId: string | undefined;
  open: boolean;
  setDescriptionId: (id: string | undefined) => void;
  setOpen: (open: boolean) => void;
  setTitleId: (id: string | undefined) => void;
  setTriggerElement: (element: HTMLElement | null) => void;
  titleId: string | undefined;
}

const DialogContext = createContext<DialogContextValue | null>(null);

interface IsolatedElementState {
  ariaHidden: string | null;
  inert: boolean;
}

const openOverlays: HTMLElement[] = [];
const isolatedElements = new Map<HTMLElement, IsolatedElementState>();

function restoreElement(element: HTMLElement, state: IsolatedElementState) {
  element.inert = state.inert;
  if (state.ariaHidden === null) {
    element.removeAttribute('aria-hidden');
  } else {
    element.setAttribute('aria-hidden', state.ariaHidden);
  }
}

function updateBackgroundIsolation() {
  const activeOverlay = openOverlays.at(-1);
  const bodyElements = Array.from(document.body.children).filter((element): element is HTMLElement => element instanceof HTMLElement);

  if (activeOverlay === undefined) {
    for (const [element, state] of isolatedElements) {
      restoreElement(element, state);
      isolatedElements.delete(element);
    }
    return;
  }

  for (const element of bodyElements) {
    if (element === activeOverlay) {
      const state = isolatedElements.get(element);
      if (state !== undefined) {
        restoreElement(element, state);
        isolatedElements.delete(element);
      }
      continue;
    }

    if (!isolatedElements.has(element)) {
      isolatedElements.set(element, { ariaHidden: element.getAttribute('aria-hidden'), inert: element.inert });
    }
    element.inert = true;
    element.setAttribute('aria-hidden', 'true');
  }

  for (const [element, state] of isolatedElements) {
    if (!bodyElements.includes(element) || element === activeOverlay) {
      restoreElement(element, state);
      isolatedElements.delete(element);
    }
  }
}

function useDialogContext() {
  const context = useContext(DialogContext);
  if (context === null) {
    throw new Error('Dialog components must be used within Dialog.');
  }
  return context;
}

export interface DialogProps {
  children: ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

export function Dialog({ children, defaultOpen = false, onOpenChange, open: controlledOpen }: DialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [titleId, setTitleId] = useState<string>();
  const [descriptionId, setDescriptionId] = useState<string>();
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);
  const previouslyOpen = useRef(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  useEffect(() => {
    if (previouslyOpen.current && !open) {
      triggerElement?.focus();
    }
    previouslyOpen.current = open;
  }, [open, triggerElement]);

  function setOpen(nextOpen: boolean) {
    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  }

  return (
    <DialogContext.Provider value={{ descriptionId, open, setDescriptionId, setOpen, setTitleId, setTriggerElement, titleId }}>
      {children}
    </DialogContext.Provider>
  );
}

interface DialogTriggerProps {
  children: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
}

export function DialogTrigger({ children }: DialogTriggerProps) {
  const { setOpen, setTriggerElement } = useDialogContext();
  if (!isValidElement(children)) {
    return null;
  }

  return cloneElement(children, {
    'aria-haspopup': 'dialog',
    onClick: (event) => {
      children.props.onClick?.(event);
      if (!event.defaultPrevented) {
        setTriggerElement(event.currentTarget);
        setOpen(true);
      }
    },
  });
}

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => element.getAttribute('aria-hidden') !== 'true');
}

export type DialogContentProps = Omit<HTMLAttributes<HTMLDivElement>, 'aria-modal' | 'role' | 'tabIndex'>;

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(function DialogContent(
  { 'aria-describedby': userDescribedBy, 'aria-labelledby': userLabelledBy, children, className, onKeyDown: userOnKeyDown, ...props },
  forwardedRef,
) {
  const { descriptionId, open, setOpen, titleId } = useDialogContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      contentRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!open || overlay === null) {
      return;
    }

    openOverlays.push(overlay);
    updateBackgroundIsolation();

    return () => {
      const overlayIndex = openOverlays.lastIndexOf(overlay);
      if (overlayIndex !== -1) {
        openOverlays.splice(overlayIndex, 1);
      }
      updateBackgroundIsolation();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  function setRefs(element: HTMLDivElement | null) {
    contentRef.current = element;
    if (typeof forwardedRef === 'function') {
      forwardedRef(element);
    } else if (forwardedRef !== null) {
      forwardedRef.current = element;
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    userOnKeyDown?.(event);
    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key !== 'Tab' || contentRef.current === null) {
      return;
    }

    const focusableElements = getFocusableElements(contentRef.current);
    if (focusableElements.length === 0) {
      event.preventDefault();
      contentRef.current.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (first === undefined || last === undefined) {
      return;
    }

    if (event.shiftKey && (document.activeElement === first || document.activeElement === contentRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className={cn('fixed inset-0 grid place-items-center p-4', LAGEN.venster)} ref={overlayRef}>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[color:color-mix(in_srgb,var(--foreground)_35%,transparent)]"
        onMouseDown={() => setOpen(false)}
      />
      <div
        className={cn('relative z-10 max-h-full w-full max-w-lg overflow-y-auto rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background)] p-5 text-[var(--foreground)] shadow-[var(--card-shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]', className)}
        ref={setRefs}
        {...props}
        aria-describedby={descriptionId ?? userDescribedBy}
        aria-labelledby={titleId ?? userLabelledBy}
        aria-modal="true"
        role="dialog"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
});

interface DialogCloseProps {
  children: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
}

export function DialogClose({ children }: DialogCloseProps) {
  const { setOpen } = useDialogContext();
  return cloneElement(children, {
    onClick: (event) => {
      children.props.onClick?.(event);
      if (!event.defaultPrevented) {
        setOpen(false);
      }
    },
  });
}

export const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function DialogHeader(
  { className, ...props },
  ref,
) {
  return <div className={cn('flex flex-col gap-1.5', className)} ref={ref} {...props} />;
});

export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function DialogFooter(
  { className, ...props },
  ref,
) {
  return <div className={cn('mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} ref={ref} {...props} />;
});

export const DialogTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(function DialogTitle(
  { className, id: _id, ...props },
  ref,
) {
  const id = useId();
  const { setTitleId } = useDialogContext();

  useEffect(() => {
    setTitleId(id);
    return () => setTitleId(undefined);
  }, [id, setTitleId]);

  return <h2 className={cn('text-base font-semibold', className)} id={id} ref={ref} {...props} />;
});

export const DialogDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(function DialogDescription(
  { className, id: _id, ...props },
  ref,
) {
  const id = useId();
  const { setDescriptionId } = useDialogContext();

  useEffect(() => {
    setDescriptionId(id);
    return () => setDescriptionId(undefined);
  }, [id, setDescriptionId]);

  return <p className={cn('text-sm text-[var(--foreground-muted)]', className)} id={id} ref={ref} {...props} />;
});
